import * as AccountDomain from "./account-domain"
import * as ChapterDomain from "./chapter-domain"
import * as Model from "../../model"

/**
 * Calcule les recettes ou dépenses de fonctionnement pour l'année `year` du
 * constat rétrospectif en fonction de `receipts`.
 * @param observations
 * @param year
 * @param receipts
 * @see getOperationExpenseObservations
 * @see getOperationReceiptsObservations
 */
export function calculateObservationOperationForYear(
	observations: Model.Observation[],
	year: number,
	receipts: boolean
): number {
	const filteredObs = receipts
	? getOperationReceiptsObservations(observations, year)
	: getOperationExpenseObservations(observations, year)

	return filteredObs.reduce((sum, o) => o && o.amount ? sum + o.amount : sum, 0)
}

/**
 * Calcule les recettes ou dépenses de fonctionnement pour les années `years` du
 * constat rétrospectif en fonction de `receipts`.
 * @param observations
 * @param years
 * @param receipts
 * @see getOperationExpenseObservations
 * @see getOperationReceiptsObservations
 * @see calculateObservationOperationForYear
 */
export function calculateObservationOperationForYears(
	observations: Model.Observation[],
	years: number[],
	receipts: boolean
): number[] {
	const filteredObs = receipts
	? getOperationReceiptsObservations(observations)
	: getOperationExpenseObservations(observations)

	return years.map(year => {
		return calculateObservationOperationForYear(filteredObs, year, receipts)
	})
}

/**
 * Calcule les dépenses de fonctionnement pour l'année `year` du constat
 * rétrospectif.
 * @param observations
 * @param year
 * @see calculateObservationOperationForYear
 */
export function calculateObservationOperationExpensesForYear(
	observations: Model.Observation[],
	year: number
): number {
	return calculateObservationOperationForYear(observations, year, false)
}

/**
 * Calcule les dépenses de fonctionnement pour les années `years` du constat
 * rétrospectif.
 * @param observations
 * @param years
 * @see getOperationExpenseObservations
 * @see calculateOperationExpensesForYear
 */
export function calculateObservationOperationExpensesForYears(
	observations: Model.Observation[],
	years: number[]
): number[] {
	return calculateObservationOperationForYears(observations, years, false)
}

/**
 * Calcule les recettes de fonctionnement pour l'année `year` du constat
 * rétrospectif.
 * @param observations
 * @param year
 * @see getOperationReceiptsObservations
 */
export function calculateObservationOperationReceiptsForYear(
	observations: Model.Observation[],
	year: number
): number {
	return calculateObservationOperationForYear(observations, year, true)
}

/**
 * Calcule les recettes de fonctionnement pour les années `years` du constat
 * rétrospectif.
 * @param observations
 * @param years
 * @see getOperationReceiptsObservations
 * @see calculateObservationOperationReceiptsForYear
 */
export function calculateObservationOperationReceiptsForYears(
	observations: Model.Observation[],
	years: number[]
): number[] {
	return calculateObservationOperationForYears(observations, years, true)
}

/**
 * Calcule pour l'année `year`, les dépenses réelles de fonctionnement de la
 * collectivité `localAuthority`.
 */
export function calculateRealOperationExpense(
	localAuthority: Model.LocalAuthority,
	year: number
): number {
	return calculateRealOperationBudget(localAuthority, year, false)
}

/**
 * Calcule pour l'année `year`, les recettes réelles de fonctionnement de la
 * collectivité `localAuthority`.
 */
export function calculateRealOperationReceipts(
	localAuthority: Model.LocalAuthority,
	year: number
): number {
	return calculateRealOperationBudget(localAuthority, year, true)
}

/**
 * Vérifie si toutes les propriétés du constat sont égales à celles passées en
 * paramètres.
 * @param observation
 * @param account
 * @param receipts
 * @param year
 */
export function equal(
	observation: Model.Observation,
	account: Model.Account,
	receipts: boolean,
	year: number
): boolean {
	return observation.account.id === account.id
	&& observation.receipts === receipts
	&& observation.year === year
}

/**
 * Filtre les opérations d'ordre et le opérations réelles.
 * @param observations
 * @param lacas
 */
export function filterRealAndOrderObservations(
	observations: Model.Observation[],
	lacas: Model.LocalAuthorityChapterAccount[]
): RealAndOrderFilteredObservations {
	return observations.reduce<RealAndOrderFilteredObservations>((filtered, o) => {
		const chapter = AccountDomain.getAttachedChapter(o.account, lacas)
		if (chapter && ChapterDomain.isOrder(chapter)) filtered.order.push(o)
		else filtered.real.push(o)
		return filtered
	}, { order: [], real: [] })
}

/**
 * Liste les constats `observations` dont le compte est un remboursement
 * d'emprunt.
 * @param observations
 *
 * @see AccountDomain.isLoanReimbursement
 */
export function findLoansReimbursementObservations(
	observations: Model.Observation[]
): Model.Observation[]
/**
 * Liste pour l'année `year`, les constats `observations` dont le compte est un
 * remboursement d'emprunt.
 * @param observations
 * @param year
 *
 * @see AccountDomain.isLoanReimbursement
 */
export function findLoansReimbursementObservations(
	observations: Model.Observation[],
	year: number
): Model.Observation[]
/**
 * Liste pour >chaque année de `years`, les constats `observations` dont le
 * compte est un remboursement d'emprunt.
 * @param observations
 * @param year
 *
 * @see AccountDomain.isLoanReimbursement
 */
export function findLoansReimbursementObservations(
	observations: Model.Observation[],
	years: number[]
): Model.Observation[][]
export function findLoansReimbursementObservations(
	observations: Model.Observation[],
	years?: number | number[]
): Model.Observation[] | Model.Observation[][]{
	if (!years)
		return observations.filter(o => o
			&& o.account
			&& AccountDomain.isLoanReimbursement(o.account))

	if ("number" === typeof years)
		return observations.filter(o => o
			&& years === o.year
			&& o.account
			&& AccountDomain.isLoanReimbursement(o.account))

	return observations.reduce((filtered, o) => {
		if (!(o && o.account && AccountDomain.isLoanReimbursement(o.account)))
			return filtered

		years.forEach((year, yearIndex) => {
			if (year === o.year) filtered[yearIndex].push(o)
		})

		return filtered

	}, years.map(() => []))
}

/**
 * Trouve parmi la liste passée en paramètre, le constat dont l'identifiant est
 * le même que celui passé en paramètre.
 * @param observations
 * @param account
 * @param receipts
 * @param year
 */
export function findObservationByAccountId(
	observations: Model.Observation[],
	accountId: number,
	receipts: boolean,
	year: number
): Model.Observation | null {
	return observations.find(o => accountId === o.account.id
		&& receipts === o.receipts
		&& year === o.year) || null
}

/**
 * Liste pour l'année `year`, les constats de dépenses réelles de fonctionnement
 * de la collectivité `localAuthority`.
 * @param localAuthority
 * @param year
 */
export function findRealOperationExpenseObservations(
	localAuthority: Model.LocalAuthority,
	year: number
): Model.Observation[] {
	return findRealOperationBudgetObservations(localAuthority, year, false)
}

/**
 * Liste pour l'année `year`, les constats de recettes réelles de fonctionnement
 *  de la collectivité `localAuthority`.
 * @param localAuthority
 * @param year
 */
export function findRealOperationReceiptsObservations(
	localAuthority: Model.LocalAuthority,
	year: number
): Model.Observation[] {
	return findRealOperationBudgetObservations(localAuthority, year, true)
}

/**
 * Filtre les opérations en fonction du chapitre passé en paramètre.
 * @param observations
 * @param chapter
 * @param lacas
 */
export function filterObservationsByChapter(
	observations: Model.Observation[],
	chapter: Model.Chapter,
	lacas: Model.LocalAuthorityChapterAccount[]
): Model.Observation[] {
	return observations.filter(o => {
		const obChapter = AccountDomain.getAttachedChapter(o.account, lacas)
		return ChapterDomain.equal(chapter, obChapter)
	})
}

/**
 * Retourne les constats d'opérations d'équipement parmi la liste spécifiée en
 * paramètre.
 * @param observations
 * @param lacas
 */
export function getEquipmentObservations(
	observations: Model.Observation[],
	lacas       : Model.LocalAuthorityChapterAccount[]
): Model.Observation[] {
	return observations.filter(observation => {
		const chapter = AccountDomain.getAttachedChapter(observation.account, lacas)
		return null !== chapter && ChapterDomain.isEquipment(chapter)
	})
}

/**
 * Retourne les constats d'opérations financières parmi la liste spécifiée en
 * paramètre.
 * @param observations
 * @param lacas
 */
export function getFinancialObservations(
	observations: Model.Observation[],
	lacas       : Model.LocalAuthorityChapterAccount[]
): Model.Observation[] {
	return observations.filter(observation => {
		const chapter = AccountDomain.getAttachedChapter(observation.account, lacas)
		return null !== chapter && ChapterDomain.isFinancial(chapter)
	})
}

/**
 * Retourne la liste des recettes de fonctionnement de l'année spécifiée en
 * paramètre parmi la liste de constats spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getInvestmentExpenseObservations(
	observations: Model.Observation[],
	year?: number
): Model.Observation[] {
	return getSpecificObservations(observations, false, false, year)
}

/**
 * Retourne la liste des recettes de fonctionnement de l'année spécifiée en
 * paramètre parmi la liste de constats spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getInvestmentReceiptsObservations(
	observations: Model.Observation[],
	year?: number
): Model.Observation[] {
	return getSpecificObservations(observations, false, true, year)
}

export function getLacaFromObsevation(
	observation: Model.Observation,
	lacas: Model.LocalAuthorityChapterAccount[]
): Model.LocalAuthorityChapterAccount | null {
	return lacas.find(laca => laca.account.id === observation.account.id) || null
}

/**
 * Retourne la liste des constats de remboursement de capitaux d'emprunts
 * parmi la liste spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getLoanRepaymentObservations(
	observations: Model.Observation[],
	year        : number
): Model.Observation[] {
	return observations.filter(observation => {
		if (year !== observation.year) return false
		if (!observation.account.number.startsWith("164")) return false
		return true
	})
}

export function getObservationFromLaca(
	laca: Model.LocalAuthorityChapterAccount,
	observations: Model.Observation[]
): Model.Observation | null {
	return observations.find(o => laca.account.id === o.account.id) || null
}

/**
 * Retourne la liste des dépenses de fonctionnement de l'année spécifiée en
 * paramètre parmi la liste de constats spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getOperationExpenseObservations(
	observations: Model.Observation[],
	year?: number
): Model.Observation[] {
	return getSpecificObservations(observations, true, false, year)
}

/**
 * Retourne la liste des recettes de fonctionnement parmi la liste de constats
 * spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getOperationReceiptsObservations(
	observations: Model.Observation[],
	year?: number
): Model.Observation[] {
	return getSpecificObservations(observations, true, true, year)
}

/**
 * Retourne les constats d'opérations d'ordre parmi la liste spécifiée en
 * paramètre.
 * @param observations
 * @param lacas
 */
export function getOrderObservations(
	observations: Model.Observation[],
	lacas       : Model.LocalAuthorityChapterAccount[]
): Model.Observation[] {
	return observations.filter(observation => {
		const chapter = AccountDomain.getAttachedChapter(observation.account, lacas)
		return null !== chapter && ChapterDomain.isOrder(chapter)
	})
}

/**
 * Retourne les constats d'opérations réelles parmi la liste spécifiée en
 * paramètre.
 * @param observations
 * @param lacas
 * @param operation Si `false`, les constats de fonctionnement sont exclus.
 * @param investment Si `false`, les constats d'investissement sont exclus.
 * @param receipts Si `false`, les constats de recettes sont exclus.
 * @param expense Si `false`, les constats de dépense sont exclus.
 * @param years Si défini, les constats sont filtrés par années.
 */
export function getRealObservations(
	observations: Model.Observation[],
	lacas: Model.LocalAuthorityChapterAccount[],
	operation = true,
	investment = true,
	receipts = true,
	expense = true,
	years?: number[]
): Model.Observation[] {
	return observations.filter(o => {
		if (!operation && o.account.operation) return false
		if (!investment && !o.account.operation) return false
		if (!receipts && o.receipts) return false
		if (!expense && !o.receipts) return false

		if (years && years.length && !years.some(y => y === o.year)) return false

		const chapter = AccountDomain.getAttachedChapter(o.account, lacas)

		return ChapterDomain.isReal(chapter)
	})
}

/**
 * Retourne une liste d'observations groupées par chapitre.
 * @param observations
 * @param lacas
 */
export function groupByChapter(
	observations: Model.Observation[],
	lacas: Model.LocalAuthorityChapterAccount[]
): ChapterGroupedObservations[] {
	return observations.reduce<ChapterGroupedObservations[]>((groups, o) => {
		const chapter = AccountDomain.getAttachedChapter(o.account, lacas)
		const group = findGroupByChapter(groups, chapter)
		if (group) group.observations.push(o)
		else groups.push({ chapter, observations: [o] })
		return groups
	}, [])
}

/**
 * Vérifie si le constat `observation` est une dépense réelle de fonctionnement.
 * @param observation
 * @param lacas
 */
export function isRealExpenseOperation(
	observation: Model.Observation,
	lacas: Model.LocalAuthorityChapterAccount[]
): boolean {
	return isRealOperation(observation, lacas, false)
}

/**
 * Vérifie si le constat `observation` est une recettes réelle de
 * fonctionnement.
 * @param observation
 * @param lacas
 */
export function isRealReceitptsOperation(
	observation: Model.Observation,
	lacas: Model.LocalAuthorityChapterAccount[]
): boolean {
	return isRealOperation(observation, lacas, true)
}

/**
 * Somme les montants des constats passés en paramètre pour les années passées
 * en paramètre.
 * @param observations
 * @param years
 */
export function sumObservationsForYears(
	observations: Model.Observation[],
	years: number[]
): number[] {
	return observations.reduce<number[]>((amounts, observation) => {
		years.forEach((year, yearIndex) => {
			if (year !== observation.year) return
			amounts[yearIndex] += observation.amount
		})
		return amounts
	}, years.map(() => 0))
}

/**
 * Somme les montants des opérations passées en paramètre.
 * @param observations
 */
export function sumObservations(observations: Model.Observation[]): number {
	return observations.reduce<number>((sum, o) => {
		return sum + o.amount
	}, 0)
}

export interface RealAndOrderFilteredObservations {
	order: Model.Observation[]
	real: Model.Observation[]
}

export interface ChapterGroupedObservations {
	chapter: Model.Chapter
	observations: Model.Observation[]
}

function calculateRealOperationBudget(
	localAuthority: Model.LocalAuthority,
	year: number,
	receipts: boolean
): number {
	let observations = findRealOperationBudgetObservations(
		localAuthority, year, receipts)
	// on supprime les comptes non inclus dans les comptes de la commune
	observations = observations.filter(o => localAuthority.accounts.some(a => a.account.id === o.account.id));
	return observations.reduce((sum, o) => o ? sum + o.amount : sum, 0)
}

/**
 * Retourne le premier groupe d'opérations correspondant au chapitre spécifié en
 * paramètre.
 * @param groups
 * @param chapter
 */
function findGroupByChapter(
	groups: ChapterGroupedObservations[],
	chapter: Model.Chapter
): ChapterGroupedObservations | null {
	return groups.find(g => ChapterDomain.equal(g.chapter, chapter)) || null
}

function findRealOperationBudgetObservations(
	localAuthority: Model.LocalAuthority,
	year: number,
	receipts: boolean
): Model.Observation[] {
	return localAuthority.observations.filter(o =>
		year === o.year
		&& o.account.operation
		&& isRealOperation(o, localAuthority.accounts, receipts))
}

/**
 * Vérifie si une liste de groupe contient le chapitre spécifié en paramètre.
 * @param groups
 * @param chapter
 */
function groupsContainChapter(
	groups: ChapterGroupedObservations[],
	chapter: Model.Chapter
): boolean {
	return groups.some(g => ChapterDomain.equal(g.chapter, chapter))
}

/**
 * Retourne la liste des opérations parmi la liste spécifiée en paramètre.
 * Si le paramètre "year" est spécifié, la liste est filtrée par année
 * correspondante.
 * Si "operation" est "true", les opérations de fonctionnement sont retournées,
 * sinon les opérations d'investissement sont retournées.
 * Si "receipts" est "true", les recettes sont retournées, sinon les dépenses
 * sont retournées.
 * @param observations
 * @param operation
 * @param receipts
 * @param year
 */
function getSpecificObservations(
	observations: Model.Observation[],
	operation: boolean,
	receipts: boolean,
	year?: number
): Model.Observation[] {
	if (year)
		return observations.filter(observation => (
			year === observation.year
			&& operation === observation.account.operation
			&& (receipts === observation.receipts
		)))

	return observations.filter(observation => (
		operation === observation.account.operation
		&& receipts === observation.receipts
	))
}

function isOrderOperation(
	observation: Model.Observation,
	lacas: Model.LocalAuthorityChapterAccount[],
	receipts?: boolean
): boolean {
	const chapter = AccountDomain.getAttachedChapter(observation.account, lacas)
	if (undefined === receipts) return ChapterDomain.isOrder(chapter)

	return receipts === observation.receipts
	&& ChapterDomain.isOrder(chapter, receipts || undefined, !receipts || undefined)
}

function isRealOperation(
	observation: Model.Observation,
	lacas: Model.LocalAuthorityChapterAccount[],
	receipts?: boolean
): boolean {
	const isOrder = isOrderOperation(observation, lacas)
	return undefined !== receipts
	? !isOrder && receipts === observation.receipts
	: !isOrder
}



// WEBPACK FOOTER //
// ./src/domain/sfp/observation-domain.ts